import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, takeUntil, skip, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MastersService } from '../../services/masters/masters.service';
import { DynamicListingComponent, ColumnConfig, ActionConfig, ActionEvent } from '../../dynamic-listing/dynamic-listing';
import { DynamicForm } from '../../dynamic-form/dynamic-form';
import { SVG_ICONS } from '../../utils/svg-icons.utils';
import * as XLSX from 'xlsx';

@Component({
  selector: 'lib-vendor-type',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DynamicListingComponent,
    DynamicForm
  ],
  templateUrl: './vendor-type.html',
})
export class VendorType implements OnInit, OnDestroy {
  masterType = 'vendor_type';
  isLoading: boolean = false;
  tableData: any[] = [];
  private destroy$ = new Subject<void>();
  
  // Pagination
  pageIndex: number = 1;
  rowsPerPage: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;  // View mode: 'list' or 'form'
  viewMode: 'list' | 'form' = 'list';
  isEditMode: boolean = false;
  selectedItemId: string | number | null = null;
  showInactives = false;
  formData: any = null;
  formGroup: FormGroup;
  // Column configurations - will be updated based on actual API response
  columnConfigs: ColumnConfig[] = [

  ];

  // Action configurations
  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE
    }
  ];  // Form field configurations - will be updated based on actual API response
  formFields: any[] = [

  ];

  constructor(
    private mastersService: MastersService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({});
  }


  async loadMasters(page: number = 1) {
    try {
      this.isLoading = true;
      const params: any = {};
      if (this.showInactives) {
        params.include_inactive = 'true';
      }
      const response: any = await lastValueFrom(
        this.mastersService.getVendorTypes(params)
      );

      // Extract data from response - the key matches the master type (plural)
      const dataKey = 'vendor_types';
      this.tableData = response?.data?.[dataKey] || [];
      this.totalCount = response?.data?.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      this.pageIndex = page;

      // Update columns and filters based on references from API
      if (response?.data?.references) {
        this.updateColumnsFromReferences(response.data.references);
      }
    } catch (error) {
      console.error('Error loading masters:', error);
    } finally {
      this.isLoading = false;
    }
  }

  updateColumnsFromReferences(references: any[]) {
    // Update columns based on references from API
    this.columnConfigs = references
      .map(ref => {
        // Map reference type to column type
        let columnType: 'text' | 'date' | 'currency' | 'number' | 'status' | 'custom' = 'text';
        if (ref.type === 'datetime' || ref.type === 'date') {
          columnType = 'date';
        } else if (ref.type === 'number') {
          columnType = 'number';
        } else if (ref.type === 'boolean') {
          columnType = 'status';
        }

        return {
          key: ref.key,
          header: ref.label,
          type: columnType,
          customField: false
        };
      });
  }

  async loadItemForEdit(id: string | number) {
    try {
      this.isLoading = true;
      // First load references for form fields
      await this.loadFormReferences();
      const response: any = await lastValueFrom(
        this.mastersService.getVendorTypeById(id)
      );
      // Handle different response structures
      this.formData = response?.data?.vendor_type || response?.data || response?.vendor_type || response;
      console.log('Form data loaded:', this.formData);
      this.initializeForm();
      // Set loading to false after form is initialized
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading item for edit:', error);
      alert('Failed to load item for editing');
      this.isLoading = false;
    }
  }

  async loadFormReferences() {
    try {
      const response: any = await lastValueFrom(
        this.mastersService.getReferences('vendor-types')
      );
      
      if (response?.data?.references) {
        // Map references to form fields format
        this.formFields = response.data.references.map((ref: any, index: number) => {
          // Map reference type to form field type
          let fieldType = 'input';
          if (ref.type === 'datetime' || ref.type === 'date') {
            fieldType = 'date';
          } else if (ref.type === 'number') {
            fieldType = 'number';
          } else if (ref.type === 'boolean') {
            fieldType = 'checkbox';
          } else if (ref.type === 'string' && ref.key.includes('description')) {
            fieldType = 'textarea';
          } else if (ref.type === 'label') {
            fieldType = 'label';
          }

          return {
            field_name: ref.key || ref.field_name || '',
            display_name: ref.label || ref.display_name || '',
            field_type: fieldType,
            data_type: ref.type || ref.data_type || 'string',
            is_mandatory: ref.required || ref.is_mandatory || false,
            is_visible: ref.is_visible !== undefined ? ref.is_visible : true,
            is_custom_field: ref.is_custom_field || false,
            tab_id: ref.tab_id || 1,
            sub_tab_id: ref.sub_tab_id || 0,
            display_order: ref.display_order !== undefined ? ref.display_order : index + 1,
            validation_rules: ref.validation_rules || '',
            help_text: ref.help_text || '',
            default_value: ref.default_value || '',
            is_active: ref.is_active !== undefined ? ref.is_active : true
          };
        });
      }
    } catch (error) {
      console.error('Error loading form references:', error);
      // Keep default form fields if references fail to load
    }
  }

    initializeForm() {
    // Initialize form group with form fields
    const formControls: any = {};
    
    // First, create controls for all formFields
    this.formFields.forEach(field => {
      const key = field.field_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      // Try to get value from formData using multiple possible keys
      let value = this.formData?.[key] || 
                  this.formData?.[field.field_name] || 
                  this.formData?.[field.field_name.toLowerCase()] ||
                  (field.field_type === 'checkbox' ? false : '');
      
      // Log for debugging
      if (!value && field.field_type !== 'checkbox') {
        console.log(`Field ${field.field_name} (key: ${key}) not found in formData:`, this.formData);
      }
      
      const validators = field.is_mandatory ? [Validators.required] : [];
      formControls[key] = [value, validators];
    });
    
    // Also ensure all keys from formData are included (in case formFields is missing some)
    if (this.formData) {
      Object.keys(this.formData).forEach(dataKey => {
        const normalizedKey = dataKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
        // Only add if not already in formControls
        if (!formControls[normalizedKey]) {
          formControls[normalizedKey] = [this.formData[dataKey], []];
        }
      });
    }
    
    this.formGroup = this.fb.group(formControls);
    console.log('Form initialized with controls:', Object.keys(formControls));
    console.log('Form data keys:', this.formData ? Object.keys(this.formData) : 'No formData');
  }

  onActionClicked(event: ActionEvent) {
    const { action, row } = event;
    
    switch (action) {
      case 'edit':
        this.editItem(row);
        break;
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'delete':
        this.deleteItem(row);
        break;
    }
  }

  editItem(row: any) {
    this.router.navigate(['/settings/masters/vendor-type/edit', row.id]);
  }

  async toggleStatus(row: any) {
    // The toggle has already updated row.is_active via two-way binding
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    if (confirm('Are you sure you want to ' + statusText + ' "' + (row.name || row.id) + '"?')) {
      try {
        this.isLoading = true;
        // Get current item data
        const currentItem: any = await lastValueFrom(
          this.mastersService.getVendorTypeById(row.id)
        );
        
        // Update only the is_active field
        const updateData = {
          ...(currentItem?.data || currentItem),
          is_active: newStatus
        };
        
        await lastValueFrom(
          this.mastersService.updateVendorType(row.id, updateData)
        );
        
        // Reload the list
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        alert('Failed to toggle status');
      } finally {
        this.isLoading = false;
      }
    }
  }


  

  async deleteItem(row: any) {
    if (confirm('Are you sure you want to delete "' + (row.name || row.id) + '"?')) {
      try {
        await lastValueFrom(
          this.mastersService.deleteVendorType(row.id)
        );
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  }

  onPageChanged(page: number) {
    this.loadMasters(page);
  }

  onShowInactivesChange() {
    this.pageIndex = 1;
    this.loadMasters(this.pageIndex);
  }

  onCreateClick() {
    this.router.navigate(['/settings/masters/vendor-type/create']);
  }

  async ngOnInit() {
    // Check current route to determine mode
    await this.checkRouteAndLoad();
    
    // Track previous URL to avoid duplicate calls
    let previousUrl = this.router.url;
    
    // Subscribe to route parameter changes
    this.route.params
      .pipe(
        skip(1), // Skip the initial emission since we already called checkRouteAndLoad above
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkRouteAndLoad();
      });
    
    // Subscribe to navigation events to handle URL changes (e.g., list -> create/edit)
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        // Only call checkRouteAndLoad if URL actually changed
        if (currentUrl !== previousUrl) {
          previousUrl = currentUrl;
          await this.checkRouteAndLoad();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkRouteAndLoad() {
    const url = this.router.url;
    // Get params from current route and child routes
    let routeParams = this.route.snapshot.params;
    let childRoute = this.route.snapshot.firstChild;
    while (childRoute) {
      routeParams = { ...routeParams, ...childRoute.params };
      childRoute = childRoute.firstChild;
    }
    
    // Also try to extract ID from URL if not in params
    let id = routeParams['id'];
    if (!id && url.includes('/edit/')) {
      const match = url.match(/\/edit\/([^\/]+)/);
      if (match) id = match[1];
    }
    if (!id && url.includes('/view/')) {
      const match = url.match(/\/view\/([^\/]+)/);
      if (match) id = match[1];
    }
    
    console.log('checkRouteAndLoad - URL:', url);
    console.log('checkRouteAndLoad - Route params:', routeParams);
    console.log('checkRouteAndLoad - Extracted ID:', id);
    
    // Check if we're in create mode
    if (url.includes('/create') || routeParams['action'] === 'create') {
      console.log('Setting to create mode');
      this.viewMode = 'form';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.formData = {};
      // Load references first, then initialize form
      this.isLoading = true;
      await this.loadFormReferences();
      this.initializeForm();
      this.isLoading = false;
    }
    // Check if we're in edit mode
    else if (id && (url.includes('/edit') || url.includes('/view'))) {
      console.log('Setting to edit mode, ID:', id);
      this.selectedItemId = id;
      this.isEditMode = url.includes('/edit');
      this.viewMode = 'form';
      await this.loadItemForEdit(id);
    }
    // Default to list mode
    else {
      console.log('Setting to list mode');
      this.viewMode = 'list';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.loadMasters();
    }
  }

  onFormFieldChanged(event: any) {
    // Handle form field changes
    if (!this.formData) {
      this.formData = {};
    }
    this.formData[event.key] = event.value;
  }

  async onFormSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    try {
      this.isLoading = true;
      const formValue = this.formGroup.value;
      
      if (this.isEditMode && this.selectedItemId) {
        await lastValueFrom(
          this.mastersService.updateVendorType(this.selectedItemId, formValue)
        );
      } else {
        await lastValueFrom(
          this.mastersService.createVendorType(formValue)
        );
      }
      this.router.navigate(['/settings/masters/vendor-type']);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/masters/vendor-type']);
  }
}